import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Bot, Check, ChevronDown, ChevronRight,
  ExternalLink, Loader2, Plus, Send, Trash2, AlertTriangle, Calculator,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import { searchNeedle, matchesSearch } from '../lib/listSearch.mjs';
import MicButton from '../components/ui/MicButton';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

async function apiPatch(path, body) {
  const res = await fetch(`/api${path}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`);
  return json;
}

async function apiPut(path, body) {
  const res = await fetch(`/api${path}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`);
  return json;
}

const STEP_LABELS = ['Project Basics', 'Scope & Materials', 'Ballpark Pricing', 'Review & Finalize'];

const DEFAULT_TERMS = 'Net 30. Material lead time confirmed at order. Freight quoted separately unless itemized above.';

// ─── Main page ───────────────────────────────────────────────────────────────

export default function ProposalBuilderPage() {
  const { leadId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [leadData, setLeadData] = useState(null); // { lead, gc_bidders, drafts }
  const [draft, setDraft]       = useState(null);
  const [config, setConfig]     = useState(null);
  const [step, setStep]         = useState(1);
  const [saving, setSaving]     = useState(false);
  const [chatOpen, setChatOpen] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatFieldsUpdated, setChatFieldsUpdated] = useState(false);

  // Per-step form state
  const [s1, setS1] = useState({}); // step1: gc_account_id override
  const [s2, setS2] = useState({}); // step2: sqft, product, application_type, construction_type, scope_notes
  const [s3, setS3] = useState({}); // step3: calc inputs + result
  const [s4, setS4] = useState({}); // step4: line_items, terms, valid_until, recipient_email, title

  // ── Bootstrap ────────────────────────────────────────────────────────────

  const loadLeadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ld, cfg] = await Promise.all([
        api.get(`/proposal-builder/lead/${leadId}`),
        api.get('/proposal-builder/pricing-config'),
      ]);
      setLeadData(ld);
      setConfig(cfg.config);

      const draftId = searchParams.get('draft');
      if (draftId) {
        const dr = await api.get(`/proposal-builder/drafts/${draftId}`);
        hydrateDraft(dr.draft, ld.lead, cfg.config);
      } else if (ld.drafts?.length > 0) {
        // auto-resume latest incomplete draft
        const incomplete = ld.drafts.find((d) => !d.proposal_id);
        if (incomplete) {
          const dr = await api.get(`/proposal-builder/drafts/${incomplete.id}`);
          hydrateDraft(dr.draft, ld.lead, cfg.config);
          setSearchParams({ draft: incomplete.id }, { replace: true });
        }
      }
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  function hydrateDraft(dr, lead, cfg) {
    setDraft(dr);
    setStep(dr.step || 1);
    // Step 1: pre-fill from lead, allow override from draft
    setS1({
      gc_account_id: dr.step1?.gc_account_id || dr.gc_account_id || '',
      ...dr.step1,
    });
    // Step 2: lead fields → draft overrides
    setS2({
      sqft: lead.square_footage || '',
      product: lead.product_interest || 'Optimal Slab',
      application_type: lead.application_type || '',
      construction_type: lead.construction_type || '',
      scope_notes: '',
      ...dr.step2,
    });
    // Step 3: defaults from config, overrides from draft
    setS3({
      dosage_kg_per_m2: cfg.dosage_kg_per_m2,
      cost_per_kg_usd: cfg.cost_per_kg_usd,
      margin_pct: cfg.margin_pct,
      waste_factor_pct: cfg.waste_factor_pct,
      calc_result: null,
      ...dr.step3,
    });
    // Step 4
    setS4({
      title: '',
      line_items: [],
      terms: DEFAULT_TERMS,
      valid_until: '',
      recipient_email: lead.email || '',
      ...dr.step4,
    });
    // Restore chat history
    if (Array.isArray(dr.chat_history)) setChatMessages(dr.chat_history);
  }

  useEffect(() => { loadLeadData(); }, [loadLeadData]);

  // ── Draft management ─────────────────────────────────────────────────────

  async function getOrCreateDraft() {
    if (draft) return draft;
    const created = await api.post('/proposal-builder/drafts', {
      lead_id: leadId,
      gc_account_id: s1.gc_account_id || null,
    });
    setDraft(created.draft);
    setSearchParams({ draft: created.draft.id }, { replace: true });
    return created.draft;
  }

  async function saveDraft(stepData = {}) {
    setSaving(true);
    try {
      const d = await getOrCreateDraft();
      const patch = {
        step,
        step1: s1, step2: s2, step3: s3, step4: s4,
        gc_account_id: s1.gc_account_id || null,
        chat_history: chatMessages.slice(-30),
        ...stepData,
      };
      const updated = await apiPatch(`/proposal-builder/drafts/${d.id}`, patch);
      setDraft(updated.draft);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function goToStep(n) {
    await saveDraft({ step: n });
    setStep(n);
  }

  // ── Auto-init draft on first GC selection ─────────────────────────────────

  async function handleStart() {
    setSaving(true);
    try {
      const created = await api.post('/proposal-builder/drafts', {
        lead_id: leadId,
        gc_account_id: s1.gc_account_id || null,
      });
      setDraft(created.draft);
      setSearchParams({ draft: created.draft.id }, { replace: true });
      const lead = leadData.lead;
      setS2((prev) => ({
        sqft: lead.square_footage || '',
        product: lead.product_interest || 'Optimal Slab',
        application_type: lead.application_type || '',
        construction_type: lead.construction_type || '',
        scope_notes: '',
        ...prev,
      }));
      setS3((prev) => ({
        dosage_kg_per_m2: config.dosage_kg_per_m2,
        cost_per_kg_usd: config.cost_per_kg_usd,
        margin_pct: config.margin_pct,
        waste_factor_pct: config.waste_factor_pct,
        ...prev,
      }));
      setStep(2);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Pricing calculator ────────────────────────────────────────────────────

  async function runCalculator() {
    if (!s2.sqft || Number(s2.sqft) <= 0) { setError('Enter square footage first.'); return; }
    setSaving(true);
    try {
      const r = await api.post('/proposal-builder/calculate', {
        sqft: Number(s2.sqft),
        // As typed: the route refuses a cleared or unusable field (400, shown by setError below)
        // instead of reading '' as 0 and pricing at a 0% margin (LP-1514).
        config_override: {
          dosage_kg_per_m2: s3.dosage_kg_per_m2,
          cost_per_kg_usd: s3.cost_per_kg_usd,
          margin_pct: s3.margin_pct,
          waste_factor_pct: s3.waste_factor_pct,
        },
      });
      setS3((prev) => ({ ...prev, calc_result: r.result }));

      // Write estimate back to lead
      if (r.result) {
        await apiPatch(`/crm/leads/${leadId}`, {
          material_cost_per_sqft: r.result.per_sqft,
          estimated_range_low: r.result.low,
          estimated_range_high: r.result.high,
          square_footage: Number(s2.sqft),
        });
      }

      // Auto-build line items for step 4
      if (r.result) {
        const lead = leadData.lead;
        const gcName = leadData.gc_bidders?.find((g) => g.id === s1.gc_account_id)?.name || '';
        setS4((prev) => ({
          ...prev,
          title: prev.title || `Optimal Slab — ${lead.company || lead.name}${gcName ? ` (for ${gcName})` : ''}`,
          line_items: prev.line_items?.length > 0 ? prev.line_items : [
            {
              description: `Optimal Slab material supply — ${s2.application_type || 'concrete treatment'} (${Number(s2.sqft).toLocaleString()} sqft / ${r.result.sqm.toLocaleString()} m²)`,
              qty: r.result.kg_needed,
              unit: 'kg',
              unit_price: parseFloat((r.result.central / r.result.kg_needed).toFixed(2)),
            },
          ],
        }));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Finalize ──────────────────────────────────────────────────────────────

  async function finalize() {
    if (!s4.line_items?.length) { setError('Add at least one line item before finalizing.'); return; }
    setSaving(true);
    try {
      const d = await getOrCreateDraft();
      await saveDraft();
      const result = await api.post(`/proposal-builder/drafts/${d.id}/finalize`, {
        title: s4.title,
        line_items: s4.line_items.filter((li) => li.description?.trim()),
        tax: 0,
        terms: s4.terms,
        scope_notes: s2.scope_notes || null,
        valid_until: s4.valid_until || null,
        recipient_email: s4.recipient_email || null,
      });
      navigate('/proposals');
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  // ── Chat field sync ───────────────────────────────────────────────────────

  function applyFieldSuggestions(suggested) {
    if (!suggested) return;
    if (suggested.sqft) setS2((p) => ({ ...p, sqft: suggested.sqft }));
    if (suggested.scope_notes) setS2((p) => ({ ...p, scope_notes: suggested.scope_notes }));
    if (suggested.product) setS2((p) => ({ ...p, product: suggested.product }));
    if (suggested.application_type) setS2((p) => ({ ...p, application_type: suggested.application_type }));
    if (suggested.construction_type) setS2((p) => ({ ...p, construction_type: suggested.construction_type }));
    if (suggested.calc_result) setS3((p) => ({ ...p, calc_result: suggested.calc_result }));
    setChatFieldsUpdated(true);
    setTimeout(() => setChatFieldsUpdated(false), 3000);
  }

  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!leadData) {
    return <div className="p-8 text-red-600">{error || 'Lead not found.'}</div>;
  }

  const { lead, gc_bidders, drafts } = leadData;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center gap-3 border-b border-[var(--border)] bg-white px-6 py-3 dark:bg-slate-900">
        <button onClick={() => navigate('/leads')} className="flex items-center gap-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          <ArrowLeft size={15} /> Leads
        </button>
        <ChevronRight size={14} className="text-gray-400" />
        <span className="text-sm text-[var(--text-secondary)]">{lead.company || lead.name}</span>
        <ChevronRight size={14} className="text-gray-400" />
        <span className="text-sm font-medium text-[var(--text-primary)]">Proposal Builder</span>
        {draft?.proposal_id && (
          <span className="ml-2 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">Finalized</span>
        )}
        {saving && <Loader2 size={14} className="ml-auto animate-spin text-gray-400" />}
        <button
          onClick={() => setChatOpen((o) => !o)}
          className="ml-auto flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:bg-gray-50"
        >
          <Bot size={14} /> {chatOpen ? 'Hide chat' : 'Show chat'}
        </button>
      </div>

      {error && (
        <div className="mx-4 mt-2 flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle size={14} /> {error}
          <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {chatFieldsUpdated && (
        <div className="mx-4 mt-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-700">
          Assistant updated form fields. Review the changes in steps 2–3.
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* ── Wizard pane ── */}
        <div className={`flex flex-1 flex-col overflow-hidden ${chatOpen ? 'border-r border-[var(--border)]' : ''}`}>
          {/* Step tabs */}
          <div className="flex gap-0 border-b border-[var(--border)] bg-gray-50 dark:bg-slate-800/60 px-4">
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              const active = step === n;
              const done = step > n;
              const canClick = draft && n <= step + 1;
              return (
                <button
                  key={n}
                  onClick={() => canClick && goToStep(n)}
                  disabled={!canClick}
                  className={`flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-medium transition-colors
                    ${active ? 'border-primary text-primary' : done ? 'border-transparent text-green-600' : 'border-transparent text-[var(--text-secondary)]'}
                    ${canClick ? 'cursor-pointer hover:text-[var(--text-primary)]' : 'cursor-not-allowed opacity-40'}`}
                >
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold
                    ${active ? 'bg-primary text-white' : done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600 dark:bg-slate-700 dark:text-slate-300'}`}>
                    {done ? <Check size={10} /> : n}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                </button>
              );
            })}
          </div>

          {/* Step content */}
          <div className="flex-1 overflow-y-auto p-6">
            {step === 1 && (
              <Step1
                lead={lead} gcBidders={gc_bidders} drafts={drafts}
                s1={s1} setS1={setS1}
                onStart={handleStart} saving={saving}
                searchParams={searchParams}
                onResumeDraft={async (id) => {
                  const dr = await api.get(`/proposal-builder/drafts/${id}`);
                  hydrateDraft(dr.draft, lead, config);
                  setSearchParams({ draft: id }, { replace: true });
                  setStep(dr.draft.step || 2);
                }}
              />
            )}
            {step === 2 && (
              <Step2
                s2={s2} setS2={setS2}
                onBack={() => goToStep(1)}
                onNext={async () => { await saveDraft({ step: 3 }); setStep(3); }}
                saving={saving}
              />
            )}
            {step === 3 && (
              <Step3
                s2={s2} s3={s3} setS3={setS3} config={config} setConfig={setConfig}
                onRunCalc={runCalculator}
                onBack={() => goToStep(2)}
                onNext={async () => { await saveDraft({ step: 4 }); setStep(4); }}
                saving={saving}
              />
            )}
            {step === 4 && (
              <Step4
                lead={lead} gcBidders={gc_bidders} draft={draft}
                s2={s2} s3={s3} s4={s4} setS4={setS4}
                onBack={() => goToStep(3)}
                onFinalize={finalize}
                saving={saving}
              />
            )}
          </div>
        </div>

        {/* ── Chat pane ── */}
        {chatOpen && (
          <ChatPanel
            leadId={leadId}
            draftId={draft?.id || null}
            messages={chatMessages}
            setMessages={setChatMessages}
            onFieldSuggestions={applyFieldSuggestions}
            lead={lead}
          />
        )}
      </div>
    </div>
  );
}

// ─── Step 1: Project Basics ──────────────────────────────────────────────────

function Step1({ lead, gcBidders, drafts, s1, setS1, onStart, saving, searchParams, onResumeDraft }) {
  const [accounts, setAccounts] = useState([]);
  const [gcSearch, setGcSearch] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  useEffect(() => {
    setLoadingAccounts(true);
    api.get('/crm/accounts').then((r) => setAccounts(r.accounts || [])).catch(() => {}).finally(() => setLoadingAccounts(false));
  }, []);

  const isPreDraft = !searchParams.get('draft');
  const incompleteDrafts = drafts.filter((d) => !d.proposal_id);

  const needle = searchNeedle(gcSearch);
  const filteredAccounts = accounts.filter((a) => matchesSearch(needle, [a.name]));

  const selectedGc = accounts.find((a) => a.id === s1.gc_account_id) || gcBidders.find((g) => g.id === s1.gc_account_id);

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Project Basics</h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">Review lead info and select the GC bidder this proposal is for.</p>
      </div>

      {/* Lead summary */}
      <div className="rounded-xl border border-[var(--border)] bg-gray-50 p-4 dark:bg-slate-800/50">
        <p className="text-sm font-semibold text-[var(--text-primary)]">{lead.name}</p>
        {lead.company && <p className="text-sm text-[var(--text-secondary)]">{lead.company}</p>}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
          {lead.email && <span>{lead.email}</span>}
          {lead.phone && <span>{lead.phone}</span>}
          {lead.square_footage && <span>{Number(lead.square_footage).toLocaleString()} sq ft</span>}
          {lead.application_type && <span>{lead.application_type}</span>}
          {lead.product_interest && <span>{lead.product_interest}</span>}
        </div>
        <div className="mt-2">
          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-slate-700 dark:text-slate-300">
            stage: {lead.stage}
          </span>
        </div>
      </div>

      {/* Existing drafts */}
      {incompleteDrafts.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[var(--text-secondary)]">Resume existing draft</p>
          {incompleteDrafts.map((d) => (
            <button
              key={d.id}
              onClick={() => onResumeDraft(d.id)}
              className="flex w-full items-center justify-between rounded-lg border border-[var(--border)] bg-white px-4 py-3 text-left text-sm hover:bg-gray-50 dark:bg-slate-900"
            >
              <span className="font-medium text-[var(--text-primary)]">
                {d.gc_account_name ? `For ${d.gc_account_name}` : 'Untitled draft'} — step {d.step}
              </span>
              <span className="text-xs text-[var(--text-secondary)]">{new Date(d.updated_at).toLocaleDateString()}</span>
            </button>
          ))}
          <p className="pt-1 text-xs text-[var(--text-secondary)]">— or start a new draft below —</p>
        </div>
      )}

      {/* GC picker */}
      <div>
        <label className="block text-sm font-medium text-[var(--text-primary)]">GC Bidder <span className="font-normal text-[var(--text-secondary)]">(optional)</span></label>
        <p className="mt-0.5 text-xs text-[var(--text-secondary)]">Which general contractor is this proposal for?</p>

        {gcBidders.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {gcBidders.map((g) => (
              <button
                key={g.id}
                onClick={() => setS1((p) => ({ ...p, gc_account_id: p.gc_account_id === g.id ? '' : g.id }))}
                className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                  s1.gc_account_id === g.id
                    ? 'border-primary bg-primary/10 font-medium text-primary'
                    : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-gray-50'
                }`}
              >
                {g.name}
              </button>
            ))}
          </div>
        )}

        <div className="mt-3 space-y-1.5">
          <input
            type="text"
            value={gcSearch}
            onChange={(e) => setGcSearch(e.target.value)}
            placeholder="Search all accounts…"
            className="w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800"
          />
          {needle && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-[var(--border)] bg-white dark:bg-slate-900">
              {filteredAccounts.slice(0, 20).map((a) => (
                <button
                  key={a.id}
                  onClick={() => { setS1((p) => ({ ...p, gc_account_id: a.id })); setGcSearch(''); }}
                  className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  <span className="font-medium text-[var(--text-primary)]">{a.name}</span>
                  {a.city && <span className="ml-2 text-xs text-[var(--text-secondary)]">{a.city}, {a.state}</span>}
                </button>
              ))}
              {filteredAccounts.length === 0 && (
                <p className="px-3 py-2 text-sm text-[var(--text-secondary)]">No accounts matching "{gcSearch.trim()}"</p>
              )}
            </div>
          )}
          {selectedGc && !needle && (
            <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-sm">
              <Check size={14} className="text-green-600" />
              <span className="font-medium text-green-700">{selectedGc.name}</span>
              <button onClick={() => setS1((p) => ({ ...p, gc_account_id: '' }))} className="ml-auto text-green-600 hover:text-green-800"><X size={13} /></button>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={onStart}
        disabled={saving}
        className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
      >
        {saving ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
        {saving ? 'Starting…' : 'Start Proposal'}
      </button>
    </div>
  );
}

// ─── Step 2: Scope & Materials ────────────────────────────────────────────────

function Step2({ s2, setS2, onBack, onNext, saving }) {
  const set = (k) => (e) => setS2((p) => ({ ...p, [k]: e.target.value }));
  const inp = 'mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';

  return (
    <div className="max-w-xl space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Scope & Materials</h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">Describe what's being treated. The assistant can fill this from your description.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <label className="block text-xs font-medium text-[var(--text-secondary)]">
          Slab area (sq ft) *
          <input type="number" className={inp} value={s2.sqft || ''} onChange={set('sqft')} placeholder="e.g. 120000" />
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">
          Product
          <input className={inp} value={s2.product || ''} onChange={set('product')} placeholder="Optimal Slab" />
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">
          Application type
          <input className={inp} value={s2.application_type || ''} onChange={set('application_type')} placeholder="e.g. polished slab, warehouse" />
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">
          Construction type
          <input className={inp} value={s2.construction_type || ''} onChange={set('construction_type')} placeholder="e.g. new construction, retrofit" />
        </label>
      </div>

      <label className="block text-xs font-medium text-[var(--text-secondary)]">
        Scope notes
        <textarea rows={4} className={inp} value={s2.scope_notes || ''} onChange={set('scope_notes')}
          placeholder="Describe the scope: location, number of floors, phasing, special requirements…" />
      </label>

      <div className="flex gap-3 pt-2">
        <button onClick={onBack} className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-gray-50">
          <ArrowLeft size={14} /> Back
        </button>
        <button
          onClick={onNext}
          disabled={saving || !s2.sqft || Number(s2.sqft) <= 0}
          className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          Next: Pricing
        </button>
      </div>
    </div>
  );
}

// ─── Step 3: Ballpark Pricing ─────────────────────────────────────────────────

function Step3({ s2, s3, setS3, config, setConfig, onRunCalc, onBack, onNext, saving }) {
  const [editingConfig, setEditingConfig] = useState(false);
  const set3 = (k) => (e) => setS3((p) => ({ ...p, [k]: e.target.value }));
  const inp = 'mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';
  const result = s3.calc_result;

  return (
    <div className="max-w-xl space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Ballpark Pricing</h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Calculate a rough material cost range for {s2.sqft ? `${Number(s2.sqft).toLocaleString()} sqft` : 'this project'}.
          Labeled "estimate — not a binding quote" on all outputs.
        </p>
      </div>

      {config?._placeholder && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Placeholder pricing active</p>
            <p className="mt-0.5 text-xs text-amber-700">
              Cost inputs below are placeholder defaults. Steve or Chase must set real cost_per_kg + margin before these
              numbers are meaningful. Edit and save via the fields below, or update Admin → Pricing Config.
            </p>
          </div>
        </div>
      )}

      {/* Config inputs */}
      <div className="rounded-xl border border-[var(--border)] bg-gray-50 p-4 dark:bg-slate-800/50">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">Pricing inputs</p>
          <button onClick={() => setEditingConfig((e) => !e)} className="text-xs text-primary hover:underline">
            {editingConfig ? 'Collapse' : 'Edit inputs'}
          </button>
        </div>
        <div className={`grid grid-cols-2 gap-3 ${editingConfig ? '' : 'pointer-events-none opacity-60'}`}>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">
            Dosage (kg/m²)
            <input type="number" step="0.1" className={inp} value={s3.dosage_kg_per_m2 ?? ''} onChange={set3('dosage_kg_per_m2')} />
          </label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">
            Cost/kg (USD)
            <input type="number" step="0.01" className={inp} value={s3.cost_per_kg_usd ?? ''} onChange={set3('cost_per_kg_usd')} />
          </label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">
            Margin (%)
            <input type="number" step="1" className={inp} value={s3.margin_pct ?? ''} onChange={set3('margin_pct')} />
          </label>
          <label className="block text-xs font-medium text-[var(--text-secondary)]">
            Waste factor (%)
            <input type="number" step="1" className={inp} value={s3.waste_factor_pct ?? ''} onChange={set3('waste_factor_pct')} />
          </label>
        </div>
      </div>

      <button
        onClick={onRunCalc}
        disabled={saving || !s2.sqft || Number(s2.sqft) <= 0}
        className="flex items-center gap-2 rounded-lg bg-slate-800 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-600"
      >
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Calculator size={15} />}
        Calculate Range
      </button>

      {result && (
        <div className="rounded-xl border border-[var(--border)] bg-white p-5 dark:bg-slate-900">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
            {result.is_placeholder ? '⚠️ PLACEHOLDER ESTIMATE — not for external use' : 'Ballpark estimate — not a binding quote'}
          </p>
          <div className="mt-3 flex gap-6">
            <div>
              <p className="text-xs text-[var(--text-secondary)]">Low</p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">{money(result.low)}</p>
            </div>
            <div className="self-center text-gray-300">—</div>
            <div>
              <p className="text-xs text-[var(--text-secondary)]">High</p>
              <p className="text-2xl font-bold text-[var(--text-primary)]">{money(result.high)}</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
            <div className="rounded-lg bg-gray-50 p-2.5 dark:bg-slate-800">
              <p className="text-[var(--text-secondary)]">Per sqft</p>
              <p className="font-semibold text-[var(--text-primary)]">${result.per_sqft}</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-2.5 dark:bg-slate-800">
              <p className="text-[var(--text-secondary)]">Material (kg)</p>
              <p className="font-semibold text-[var(--text-primary)]">{result.kg_needed.toLocaleString()}</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-2.5 dark:bg-slate-800">
              <p className="text-[var(--text-secondary)]">Area (m²)</p>
              <p className="font-semibold text-[var(--text-primary)]">{result.sqm.toLocaleString()}</p>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-[var(--text-secondary)]">
            Dosage: {result.config_used.dosage_kg_per_m2} kg/m² · Cost: ${result.config_used.cost_per_kg_usd}/kg · Margin: {result.config_used.margin_pct}% · Waste: {result.config_used.waste_factor_pct}%
          </p>
          <p className="mt-1 text-[11px] font-medium text-amber-600">Saved to lead: estimated range ${money(result.low)} – ${money(result.high)}</p>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button onClick={onBack} className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-gray-50">
          <ArrowLeft size={14} /> Back
        </button>
        <button
          onClick={onNext}
          disabled={saving}
          className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          Next: Review
        </button>
      </div>
    </div>
  );
}

// ─── Step 4: Review & Finalize ────────────────────────────────────────────────

function Step4({ lead, gcBidders, draft, s2, s3, s4, setS4, onBack, onFinalize, saving }) {
  const set4 = (k) => (e) => setS4((p) => ({ ...p, [k]: e.target.value }));
  const inp = 'mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm dark:bg-slate-800';
  const result = s3.calc_result;

  const gcName = gcBidders?.find((g) => g.id === s4.gc_account_id)?.name;

  function addLine() {
    setS4((p) => ({ ...p, line_items: [...(p.line_items || []), { description: '', qty: 1, unit: '', unit_price: '' }] }));
  }
  function setLine(i, k) {
    return (e) => setS4((p) => ({
      ...p,
      line_items: (p.line_items || []).map((li, j) => j === i ? { ...li, [k]: e.target.value } : li),
    }));
  }
  function removeLine(i) {
    setS4((p) => ({ ...p, line_items: (p.line_items || []).filter((_, j) => j !== i) }));
  }

  const subtotal = (s4.line_items || []).reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0), 0);

  return (
    <div className="max-w-xl space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Review & Finalize</h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Review the proposal details. "Create Draft" saves it as a draft proposal — you still send it manually.
        </p>
      </div>

      {draft?.proposal_id && (
        <div className="flex items-center gap-2 rounded-xl border border-green-300 bg-green-50 p-4">
          <Check size={16} className="text-green-600" />
          <p className="text-sm font-semibold text-green-700">Proposal created.{' '}
            <a href="/proposals" className="underline">View in Proposals →</a>
          </p>
        </div>
      )}

      <label className="block text-xs font-medium text-[var(--text-secondary)]">
        Proposal title
        <input className={inp} value={s4.title || ''} onChange={set4('title')} placeholder="Optimal Slab — Building 4 warehouse floor" />
      </label>

      <div className="grid grid-cols-2 gap-4">
        <label className="block text-xs font-medium text-[var(--text-secondary)]">
          Valid until
          <input type="date" className={inp} value={s4.valid_until || ''} onChange={set4('valid_until')} />
        </label>
        <label className="block text-xs font-medium text-[var(--text-secondary)]">
          Recipient email
          <input type="email" className={inp} value={s4.recipient_email || ''} onChange={set4('recipient_email')} placeholder={lead.email || ''} />
        </label>
      </div>

      {/* Pricing summary */}
      {result && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-semibold text-amber-800">Pricing from Step 3 (ballpark{result.is_placeholder ? ' — PLACEHOLDER' : ''})</p>
          <p className="mt-1 text-sm text-amber-700">Range: {money(result.low)} – {money(result.high)} · ${result.per_sqft}/sqft</p>
        </div>
      )}

      {/* Line items */}
      <div>
        <p className="mb-2 text-xs font-medium text-[var(--text-secondary)]">Line items</p>
        <div className="space-y-2">
          {(s4.line_items || []).map((li, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className={`${inp} mt-0 flex-1`} placeholder="Description" value={li.description} onChange={setLine(i, 'description')} />
              <input className={`${inp} mt-0 w-20`} type="number" placeholder="Qty" value={li.qty} onChange={setLine(i, 'qty')} />
              <input className={`${inp} mt-0 w-16`} placeholder="Unit" value={li.unit} onChange={setLine(i, 'unit')} />
              <input className={`${inp} mt-0 w-28`} type="number" step="0.01" placeholder="$/unit" value={li.unit_price} onChange={setLine(i, 'unit_price')} />
              <button onClick={() => removeLine(i)} className="rounded p-1 text-gray-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <button onClick={addLine} className="text-sm text-primary hover:underline">+ Add line</button>
          <p className="text-sm text-[var(--text-secondary)]">
            Subtotal <b className="text-[var(--text-primary)]">${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</b>
          </p>
        </div>
      </div>

      <label className="block text-xs font-medium text-[var(--text-secondary)]">
        Scope notes (shown to customer)
        <textarea rows={3} className={inp} value={s2.scope_notes || ''} readOnly />
      </label>

      <label className="block text-xs font-medium text-[var(--text-secondary)]">
        Terms
        <textarea rows={2} className={inp} value={s4.terms || ''} onChange={set4('terms')} />
      </label>

      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <b>Note:</b> Clicking "Create Proposal Draft" creates a draft proposal in the Proposals module. You still need to go to <a href="/proposals" className="underline">Proposals</a> to send it to the customer.
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={onBack} className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-gray-50">
          <ArrowLeft size={14} /> Back
        </button>
        <button
          onClick={onFinalize}
          disabled={saving || !!draft?.proposal_id}
          className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {draft?.proposal_id ? 'Already created' : 'Create Proposal Draft'}
        </button>
      </div>
    </div>
  );
}

// ─── Chat panel ───────────────────────────────────────────────────────────────

function ChatPanel({ leadId, draftId, messages, setMessages, onFieldSuggestions, lead }) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const SUGGESTIONS = [
    'Calculate pricing for this project',
    'Fill in the scope from what I know',
    'What dosage should I use for warehouse polished slab?',
  ];

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Hi! I'm your Proposal Assistant for this proposal${lead?.company ? ` to ${lead.company}` : ''}. Describe the project and I'll help fill out the steps and run the ballpark calculator.`,
      }]);
    }
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function send(text) {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: msg };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setLoading(true);

    try {
      const history = updated.slice(-8).map((m) => ({ role: m.role, content: m.content }));
      const res = await api.post('/proposal-builder/chat', {
        message: msg,
        history: history.slice(0, -1),
        lead_id: leadId,
        draft_id: draftId,
      });
      const assistantMsg = {
        role: 'assistant',
        content: res.answer || "I couldn't process that. Try describing the project size and type.",
        tools_used: res.tools_used || [],
        suggested_fields: res.suggested_fields || null,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      if (res.suggested_fields) onFieldSuggestions(res.suggested_fields);
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'Something went wrong. Please try again.' }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }

  return (
    <div className="flex w-80 shrink-0 flex-col overflow-hidden bg-white dark:bg-slate-900 xl:w-96">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-[var(--border)] px-4 py-3">
        <Bot size={16} className="text-primary" />
        <span className="text-sm font-semibold text-[var(--text-primary)]">Proposal Assistant</span>
        <span className="ml-1 text-xs text-[var(--text-secondary)]">fills as you chat</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[90%] rounded-xl px-3 py-2 text-sm ${
              msg.role === 'user'
                ? 'bg-primary text-white rounded-br-none'
                : 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-gray-200 rounded-bl-none'
            }`}>
              <span className="whitespace-pre-wrap">{msg.content}</span>
              {msg.suggested_fields && (
                <p className="mt-1 text-[11px] opacity-70">↑ Updated form fields</p>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="rounded-xl rounded-bl-none bg-gray-100 px-3 py-2 dark:bg-slate-800">
              <Loader2 size={14} className="animate-spin text-gray-400" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Suggestion chips */}
      {messages.length <= 2 && !loading && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              className="rounded-full border border-[var(--border)] px-2.5 py-1 text-[11px] text-[var(--text-secondary)] hover:bg-gray-50 hover:text-[var(--text-primary)]"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-[var(--border)] px-3 py-3 flex items-end gap-2">
        <textarea
          ref={inputRef}
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Describe the project…"
          className="flex-1 resize-none rounded-lg border border-[var(--border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 dark:bg-slate-800 max-h-24 overflow-y-auto"
          style={{ minHeight: '36px' }}
        />
        <MicButton onTranscript={(t) => setInput((p) => (p ? p + ' ' + t : t).trim())} size={14} />
        <button
          onClick={() => send()}
          disabled={loading || !input.trim()}
          className="rounded-lg bg-primary p-2 text-white hover:opacity-90 disabled:opacity-40"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}
